import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MinusIcon, PlusIcon } from 'lucide-react';
import { comparisonRows, plans, pricingFaqs } from '../../data/plans';
import type { BillingPeriod, PlanId } from '../../types/plan';
import { PlanRow } from '../../components/PlanRow';
import { GridBackdrop } from '../../components/GridBackdrop';
import { useAccount } from '../../contexts/AccountContext';
export function Pricing() {
  const navigate = useNavigate();
  const {
    setPlanId,
    setBillingPeriod
  } = useAccount();
  const [period, setPeriod] = useState<BillingPeriod>('monthly');
  const [domains, setDomains] = useState(3);
  const [wantSsl, setWantSsl] = useState(true);
  const [wantUptime, setWantUptime] = useState(true);
  const checksPerDomain = (wantSsl ? 1 : 0) + (wantUptime ? 1 : 0);
  const servicesNeeded = domains * checksPerDomain;
  const recommendedId = useMemo<PlanId | null>(() => {
    if (servicesNeeded === 0) {
      return null;
    }
    const fit = plans.find(plan => plan.services >= servicesNeeded);
    return fit ? fit.id : 'pro';
  }, [servicesNeeded]);
  const fitPlan = plans.find(plan => plan.id === recommendedId);
  function choose(planId: PlanId) {
    setPlanId(planId);
    setBillingPeriod(period);
    navigate('/signup');
  }
  return <>
      <section className="relative overflow-hidden bg-shell" data-id="element-1711" data-mp="idmqpnh16fzm">
        <GridBackdrop data-id="element-1712" data-mp="weh0a9ftemzv" />
        <div className="relative mx-auto grid w-full max-w-page items-center gap-12 px-5 py-16 sm:px-8 lg:grid-cols-[0.95fr_1.05fr] lg:py-20" data-id="element-1713" data-mp="wdxlh1lgoeq2">
          <div data-id="element-1714" data-mp="rf4591esi9m7">
            <p className="flex items-center gap-2.5 font-mono text-xs uppercase tracking-[0.16em] text-brand-bright" data-id="element-1715" data-mp="rdjkg3ia251j">
              <span className="h-px w-8 bg-brand-bright/60" aria-hidden="true" data-id="element-1716" data-mp="115fkvl7u678x" />
              1 check · 1 domain · 1 service
            </p>
            <h1 className="mt-5 text-[2.5rem] font-semibold leading-[1.04] tracking-[-0.02em] text-white sm:text-[3.2rem]" data-id="element-1717" data-mp="1f5qdotsphvwq">
              Pay for checks,
              <span className="block text-white/50" data-id="element-1718" data-mp="7spm9spa419d">not for seats.</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/65" data-id="element-1719" data-mp="crk2qnzrxdqb">
              Every plan sells a number of services. One check on one domain is
              one service, so you decide whether that buys more sites or deeper
              cover on the sites you have.
            </p>
            <p className="mt-6 text-[13px] text-white/50" data-id="element-1720" data-mp="qixkpxi1a3j8">
              Size it on the right — we will point at the plan that fits.
            </p>
          </div>

          <div className="overflow-hidden rounded-card border border-line bg-surface shadow-pop" data-id="element-1721" data-mp="vggykwatkean">
            <div className="flex items-center justify-between gap-4 border-b border-line px-6 py-3.5" data-id="element-1722" data-mp="10hmrau4hc1k6">
              <div className="flex items-center gap-2.5" data-id="element-1723" data-mp="14hx9r69gkkfx">
                <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-brand" data-id="element-1724" data-mp="3299ml7vemimg" />
                <h2 className="text-[13px] font-semibold text-ink" data-id="element-1725" data-mp="369tik848rg70">
                  Size your plan
                </h2>
              </div>
              <span className="font-mono text-[11px] text-ink-soft" data-id="element-1726" data-mp="2yrc4rppc1f88">
                estimate
              </span>
            </div>

            <div className="mt-1 flex items-center justify-between gap-4 border-b border-line px-6 pb-5 pt-5" data-id="element-1727" data-mp="2txr7y0zh6ram">
              <div data-id="element-1728" data-mp="2kz5qk95qpibf">
                <p className="text-sm font-medium text-ink" data-id="element-1729" data-mp="2kzp8vqzay8pz">
                  Domains to monitor
                </p>
                <p className="mt-0.5 text-xs text-ink-soft" data-id="element-1730" data-mp="3hodchia9ttfv">
                  Sites, subdomains, or both
                </p>
              </div>
              <div className="flex items-center gap-1.5" data-id="element-1731" data-mp="3co8xxxsxb8sa">
                <button type="button" onClick={() => setDomains(n => Math.max(1, n - 1))} aria-label="One fewer domain" className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-line-strong text-ink transition-colors duration-150 ease-snap hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-1732" data-mp="38novcepkzcyr">
                  <MinusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1733" data-mp="3ky677c4acgl5" />
                </button>
                <span className="w-10 text-center font-mono text-lg font-medium text-ink" aria-live="polite" data-id="element-1734" data-mp="377ax5wi1vtfz">
                  {domains}
                </span>
                <button type="button" onClick={() => setDomains(n => Math.min(30, n + 1))} aria-label="One more domain" className="inline-flex h-8 w-8 items-center justify-center rounded-control border border-line-strong text-ink transition-colors duration-150 ease-snap hover:bg-canvas focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand" data-id="element-1735" data-mp="2tco73w8trl5">
                  <PlusIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-1736" data-mp="2jh72iemub414" />
                </button>
              </div>
            </div>

            <fieldset className="mt-5 px-6" data-id="element-1737" data-mp="2ay3hu5te8wzn">
              <legend className="font-mono text-[11px] uppercase tracking-[0.1em] text-ink-soft" data-id="element-1738" data-mp="26yl6a0ngpy9h">
                Checks on each domain
              </legend>
              <div className="mt-2.5 grid gap-2 sm:grid-cols-2" data-id="element-1739" data-mp="2kxb3dgaa7xxu">
                {([{
                label: 'Certificate expiry',
                value: wantSsl,
                set: setWantSsl
              }, {
                label: 'Uptime',
                value: wantUptime,
                set: setWantUptime
              }] as const).map(check => <button key={check.label} type="button" aria-pressed={check.value} onClick={() => check.set(current => !current)} className={`rounded-control border px-3.5 py-3 text-left text-[13px] font-medium transition-colors duration-150 ease-snap ${check.value ? 'border-brand bg-brand-soft text-brand-ink' : 'border-line-strong text-ink-muted hover:border-ink-soft hover:text-ink'}`} data-id="element-1740" data-mp="1x8i69ghxbk1n">
                    <span className="flex items-center gap-2" data-id="element-1741" data-mp="3id8vecvaxtif">
                      <span className={`flex h-3.5 w-3.5 items-center justify-center rounded-sm border ${check.value ? 'border-brand bg-brand' : 'border-line-strong'}`} aria-hidden="true" data-id="element-1742" data-mp="3icp9kl2j6kz4">
                        {check.value && <span className="h-1.5 w-1.5 rounded-[1px] bg-white" data-id="element-1743" data-mp="2zkmqz46bwum8" />}
                      </span>
                      {check.label}
                    </span>
                    <span className="mt-1 block pl-[22px] text-xs font-normal text-ink-soft" data-id="element-1744" data-mp="eyuyrmrewii6">
                      1 service each
                    </span>
                  </button>)}
              </div>
            </fieldset>

            <div className="mx-6 mb-6 mt-5 rounded-control border border-line bg-canvas px-4 py-4" data-id="element-1745" data-mp="fa40o6w35baa">
              {servicesNeeded === 0 ? <p className="text-[13px] text-ink-muted" data-id="element-1746" data-mp="b920nnucgv79">
                  Switch on at least one check to see which plan fits.
                </p> : <>
                  <p className="font-mono text-[13px] text-ink-muted" data-id="element-1747" data-mp="19jjff5tjniw">
                    {domains} {domains === 1 ? 'domain' : 'domains'} ×{' '}
                    {checksPerDomain}{' '}
                    {checksPerDomain === 1 ? 'check' : 'checks'} ={' '}
                    <span className="font-medium text-ink" data-id="element-1748" data-mp="1qqc5keyzoyp4">
                      {servicesNeeded} services
                    </span>
                  </p>
                  <p className="mt-2 text-[13px] leading-relaxed text-ink" data-id="element-1749" data-mp="1mmcw2gc4n059">
                    {servicesNeeded > 15 ? 'That is past Pro’s 15 services — talk to us about a custom allowance.' : `${fitPlan?.name} covers that, at $${fitPlan?.monthlyPrice} a month.`}
                  </p>
                </>}
            </div>
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-canvas" data-id="element-1750" data-mp="1m9hnjsvy10f7">
        <div className="mx-auto w-full max-w-page px-5 py-14 sm:px-8" data-id="element-1751" data-mp="1m7wvc9x3ptki">
          <div className="flex flex-wrap items-end justify-between gap-4" data-id="element-1752" data-mp="1r9dx07netk25">
            <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1753" data-mp="1d930op3bzd9l">
              Three plans
            </h2>
            <div className="inline-flex items-center gap-1 rounded-control border border-line-strong bg-surface p-1" data-id="element-1754" data-mp="1i866ibr4xnim">
              {(['monthly', 'annual'] as BillingPeriod[]).map(option => <button key={option} type="button" onClick={() => setPeriod(option)} aria-pressed={period === option} className={`rounded-[5px] px-3.5 py-1.5 text-[13px] font-medium transition-colors duration-150 ease-snap ${period === option ? 'bg-canvas text-ink' : 'text-ink-muted hover:text-ink'}`} data-id="element-1755" data-mp="3cl5i7tse9xkm">
                  {option === 'monthly' ? 'Monthly' : 'Yearly · 2 months free'}
                </button>)}
            </div>
          </div>

          <div className="mt-8 space-y-4" data-id="element-1756" data-mp="ib49wllsx2j9">
            {plans.map(plan => <PlanRow key={plan.id} plan={plan} period={period} fits={recommendedId === plan.id} onSelect={() => choose(plan.id)} data-id="element-1757" data-mp="i91jw1knf9ej" />)}
          </div>
        </div>
      </section>

      <section className="border-b border-line bg-surface" data-id="element-1758" data-mp="2g38ji1d2z4hn">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8" data-id="element-1759" data-mp="2v9jj3b782ii4">
          <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1760" data-mp="2r750ms7gtkrd">
            Everything compared
          </h2>
          <div className="mt-8 overflow-x-auto" data-id="element-1761" data-mp="2m8lh0ygcgl26">
            <table className="w-full min-w-[640px] border-collapse text-left" data-id="element-1762" data-mp="2h8pcmy3qirm3">
              <caption className="sr-only" data-id="element-1763" data-mp="2hb20tt035y7l">
                Feature comparison across the Starter, Standard and Pro plans
              </caption>
              <thead data-id="element-1764" data-mp="39x4ml680ejqk">
                <tr className="border-b border-line-strong" data-id="element-1765" data-mp="39xwjckdcenif">
                  <th scope="col" className="w-[34%] pb-3 text-[13px] font-semibold text-ink" data-id="element-1766" data-mp="3564ohxn5lvmh">
                    Feature
                  </th>
                  {plans.map(plan => <th key={plan.id} scope="col" className={`px-3 pb-3 pt-2 text-[13px] font-semibold text-ink ${plan.recommended ? 'rounded-t bg-brand-soft' : ''}`} data-id="element-1767" data-mp="1ivzues64n6b">
                      {plan.name}
                      <span className="ml-1.5 font-mono text-[13px] font-normal text-ink-soft" data-id="element-1768" data-mp="e0gkmn6mb4fp">
                        ${plan.monthlyPrice}
                      </span>
                    </th>)}
                </tr>
              </thead>
              <tbody data-id="element-1769" data-mp="n0bxcwftn7hu">
                {comparisonRows.map(row => <tr key={row.label} className="border-b border-line" data-id="element-1770" data-mp="rywp07h0ciuo">
                    <th scope="row" className="py-3 pr-4 text-[13px] font-normal text-ink-muted" data-id="element-1771" data-mp="wqyb0l3z9nxw">
                      {row.label}
                      {row.hint && <span className="mt-0.5 block text-xs text-ink-soft" data-id="element-1772" data-mp="16q5l8zff12gs">
                          {row.hint}
                        </span>}
                    </th>
                    {plans.map(plan => <td key={plan.id} className={`px-3 py-3 font-mono text-[13px] text-ink ${plan.recommended ? 'bg-brand-soft' : ''}`} data-id="element-1773" data-mp="37opney58kyj0">
                        {row.value(plan)}
                      </td>)}
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-canvas" data-id="element-1774" data-mp="3li8lmj2htl5n">
        <div className="mx-auto w-full max-w-page px-5 py-16 sm:px-8" data-id="element-1775" data-mp="3cltj380rukc">
          <h2 className="text-[1.85rem] font-semibold leading-tight tracking-tight text-ink" data-id="element-1776" data-mp="3aricgmjvgtg">
            The questions this pricing raises
          </h2>
          <dl className="mt-8 grid gap-x-12 gap-y-6 md:grid-cols-2" data-id="element-1777" data-mp="253gv6bowqnyz">
            {pricingFaqs.map(faq => <div key={faq.question} className="border-t border-line pt-5" data-id="element-1778" data-mp="213yiu4b5jl7r">
                <dt className="text-[15px] font-semibold text-ink" data-id="element-1779" data-mp="1qygg897ant6e">
                  {faq.question}
                </dt>
                <dd className="mt-2 text-sm leading-relaxed text-ink-muted" data-id="element-1780" data-mp="1uz0ejg3lt2u4">
                  {faq.answer}
                </dd>
              </div>)}
          </dl>
        </div>
      </section>
    </>;
}